import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {tap} from 'rxjs/operators';
import {Auth} from '../actions/auth.actions';
import {ProcessFlow} from '../models/process.flow/processFlow.model';
import {ProcessFlowService} from '../services/processFlow.service';
import {JobProcessFlow} from '../models/process.flow/jobProcessFlow.model';
import {JobProcess} from '../models/process.flow/jobProcess.model';
import {ProcessFlows} from '../actions/processFlow.actions';
import ClearState = Auth.ClearState;
import CreateProcessFlow = ProcessFlows.CreateProcessFlow;
import UpdateProcessFlow = ProcessFlows.UpdateProcessFlow;
import DeleteProcessFlow = ProcessFlows.DeleteProcessFlow;
import GetProcessFlows = ProcessFlows.GetProcessFlows;
import GetProcessFlow = ProcessFlows.GetProcessFlow;
import GetJobProcessFlows = ProcessFlows.GetJobProcessFlows;
import GetJobProcessFlow = ProcessFlows.GetJobProcessFlow;
import GetMyProcesses = ProcessFlows.GetMyProcesses;
import SelectMyCurrentProcess = ProcessFlows.SelectMyCurrentProcess;
import SignOffProcess = ProcessFlows.SignOffProcess;
import StartProcess = ProcessFlows.StartProcess;
import GetNextProcess = ProcessFlows.GetNextProcess;
import UpdateProcess = ProcessFlows.UpdateProcess;
import AssignProcess = ProcessFlows.AssignProcess;

export interface ProcessFlowStateModel {
  processFlows: Array<ProcessFlow>;
  currentProcessFlow: ProcessFlow;
  jobProcessFlows: Array<JobProcessFlow>;
  currentJobProcessFlow: JobProcessFlow;
  myProcesses: Array<JobProcess>;
  myCurrentProcess: JobProcess;
}

@State<ProcessFlowStateModel>({
  name: 'processFlow',
  defaults: {
    processFlows: [],
    currentProcessFlow: null,
    jobProcessFlows: [],
    currentJobProcessFlow: null,
    myProcesses: [],
    myCurrentProcess: null,
  }
})
@Injectable()
export class ProcessFlowState {
  constructor(private processFlowService: ProcessFlowService
  ) {
  }

  @Selector()
  static processFlows(state: ProcessFlowStateModel): Array<ProcessFlow> {
    return state.processFlows;
  }

  @Selector()
  static currentProcessFlow(state: ProcessFlowStateModel): ProcessFlow {
    return state.currentProcessFlow;
  }

  @Selector()
  static jobProcessFlows(state: ProcessFlowStateModel): Array<JobProcessFlow> {
    return state.jobProcessFlows;
  }

  @Selector()
  static currentJobProcessFlow(state: ProcessFlowStateModel): JobProcessFlow {
    return state.currentJobProcessFlow;
  }

  @Selector()
  static myProcesses(state: ProcessFlowStateModel): Array<JobProcess> {
    return state.myProcesses;
  }

  @Selector()
  static myCurrentProcess(state: ProcessFlowStateModel): JobProcess {
    return state.myCurrentProcess;
  }

  @Action(CreateProcessFlow)
  createProcessFlow(ctx: StateContext<ProcessFlowStateModel>, action: CreateProcessFlow): Observable<ProcessFlow> {
    return this.processFlowService.createProcessFlow(action.processFlow).pipe(
      tap(unit => {
        ctx.patchState({
          currentProcessFlow: unit,
        });
      })
    );
  }

  @Action(UpdateProcessFlow)
  updateProcessFlow(ctx: StateContext<ProcessFlowStateModel>, action: UpdateProcessFlow): Observable<ProcessFlow> {
    return this.processFlowService.updateProcessFlow(action.processFlow).pipe(
      tap(unit => {
        ctx.patchState({
          currentProcessFlow: unit
        });
      })
    );
  }

  @Action(DeleteProcessFlow)
  deleteProcessFlow(ctx: StateContext<ProcessFlowStateModel>, action: DeleteProcessFlow): Observable<ProcessFlow> {
    const flows = ctx.getState().processFlows;
    const index = flows.indexOf(action.processFlow);
    return this.processFlowService.deleteProcessFlow(action.processFlow.id).pipe(
      tap(flow => {
        ctx.patchState({
          currentProcessFlow: null,
          processFlows: [
            ...flows.slice(0, index),
            ...index === flows.length - 1 ? [] : flows.slice(index + 1)
          ]
        });
      })
    );
  }

  @Action(GetProcessFlows)
  getProcessFlows(ctx: StateContext<ProcessFlowStateModel>, action: GetProcessFlows): Observable<ProcessFlow[]> {
    if (!action.force) {
      const processFlows = ctx.getState().processFlows;
      if (processFlows.length > 0) {
        return of(processFlows);
      }
    }
    return this.processFlowService.getProcessFlows().pipe(
      tap(flows => {
        ctx.patchState({
          processFlows: flows
        });
      })
    );
  }

  @Action(GetProcessFlow)
  getProcessFlow(ctx: StateContext<ProcessFlowStateModel>, action: GetProcessFlow): Observable<ProcessFlow> {
    if (!action.force) {
      const processFlow = ctx.getState().currentProcessFlow;
      if (processFlow && processFlow.id === action.id) {
        return of(processFlow);
      }

      const cachedFlow = ctx.getState().processFlows.find(f => f.id === action.id);
      if (cachedFlow) {
        ctx.patchState({
          currentProcessFlow: cachedFlow
        });
        return of(cachedFlow);
      }
    }
    return this.processFlowService.getProcessFlow(action.id).pipe(
      tap(flow => {
        ctx.patchState({
          currentProcessFlow: flow
        });
      })
    );
  }

  @Action(GetJobProcessFlows)
  getJobProcessFlows(ctx: StateContext<ProcessFlowStateModel>, action: GetJobProcessFlows): Observable<JobProcessFlow[]> {
    if (!action.force) {
      const processFlows = ctx.getState().jobProcessFlows;
      if (processFlows.length > 0) {
        return of(processFlows);
      }
    }
    return this.processFlowService.getJobProcessFlows().pipe(
      tap(flows => {
        ctx.patchState({
          jobProcessFlows: flows
        });
      })
    );
  }

  @Action(GetJobProcessFlow)
  getJobProcessFlow(ctx: StateContext<ProcessFlowStateModel>, action: GetJobProcessFlow): Observable<JobProcessFlow> {
    if (!action.force) {
      const processFlow = ctx.getState().currentJobProcessFlow;
      if (processFlow && processFlow.id === action.id) {
        return of(processFlow);
      }

      const cachedFlow = ctx.getState().jobProcessFlows.find(f => f.id === action.id);
      if (cachedFlow) {
        ctx.patchState({
          currentJobProcessFlow: cachedFlow
        });
        return of(cachedFlow);
      }
    }
    return this.processFlowService.getJobProcessFlow(action.jobId, action.id).pipe(
      tap(flow => {
        ctx.patchState({
          currentJobProcessFlow: flow
        });
      })
    );
  }

  @Action(GetMyProcesses)
  getMyProcesses(ctx: StateContext<ProcessFlowStateModel>, action: GetMyProcesses): Observable<JobProcess[]> {
    if (!action.force) {
      const processes = ctx.getState().myProcesses;
      if (processes.length > 0) {
        return of(processes);
      }
    }
    return this.processFlowService.getMyProcesses().pipe(
      tap(processes => {
        ctx.patchState({
          myProcesses: processes
        });
      })
    );
  }

  @Action(SelectMyCurrentProcess)
  selectMyProcess(ctx: StateContext<ProcessFlowStateModel>, action: SelectMyCurrentProcess): Observable<JobProcess> {
    ctx.patchState({
      myCurrentProcess: action.process
    });
    return of(action.process);
  }

  @Action(SignOffProcess)
  signOffProcess(ctx: StateContext<ProcessFlowStateModel>, action: SignOffProcess): Observable<JobProcess> {
    return this.processFlowService.signOffProcess(action.process).pipe(
    );
  }

  @Action(StartProcess)
  startProcess(ctx: StateContext<ProcessFlowStateModel>, action: StartProcess): Observable<JobProcessFlow> {
    return this.processFlowService.startProcess(action.process).pipe(
    );
  }

  @Action(AssignProcess)
  assignProcess(ctx: StateContext<ProcessFlowStateModel>, action: AssignProcess): Observable<JobProcessFlow> {
    return this.processFlowService.assignProcess(action.process, action.user).pipe(
      tap(jobProcessFlow => {
        ctx.patchState({
          currentJobProcessFlow: jobProcessFlow
        });
      })
    );
  }

  @Action(GetNextProcess)
  getNextProcess(ctx: StateContext<ProcessFlowStateModel>, action: GetNextProcess): Observable<JobProcess> {
    return this.processFlowService.getNextProcess().pipe(
      tap(process => {
        ctx.patchState({
          myCurrentProcess: process
        });
      })
    );
  }

  @Action(UpdateProcess)
  updateProcess(ctx: StateContext<ProcessFlowStateModel>, action: UpdateProcess): Observable<JobProcess> {
    return this.processFlowService.updateProcess(action.process, action.checkpoints).pipe(
      tap(process => {
        ctx.patchState({
          myCurrentProcess: process
        });
      })
    );
  }

  @Action(ClearState)
  clearState(ctx: StateContext<ProcessFlowStateModel>): void {
    ctx.setState({
      processFlows: [],
      currentProcessFlow: null,
      jobProcessFlows: [],
      currentJobProcessFlow: null,
      myProcesses: [],
      myCurrentProcess: null,
    });
  }
}
